import { createContext, useContext, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API } from '../api';

const ApiContext = createContext();

const TOKEN_LIFETIME = 1000 * 60 * 5; // 5 minutes

async function authenticate() {
  const res = await API.post('/auth', {
    username: process.env.REACT_APP_API_USERNAME,
    password: process.env.REACT_APP_API_PASSWORD,
  });
  return res.data.bearer;
}

export function ApiProvider({ children }) {
  const tokenRef = useRef(null);

  // useQuery handles fetching, caching and states automatically
  const { data: token, isLoading: loadingToken, error, refetch } = useQuery({
    queryKey: ['authToken'],
    queryFn: authenticate,
    staleTime: TOKEN_LIFETIME,
    retry: 1,
    refetchInterval: TOKEN_LIFETIME - 10000, // refresh 10 seconds before expiry
    refetchIntervalInBackground: true,
  });

  if (token) tokenRef.current = token;

  useEffect(() => {
    const interceptor = API.interceptors.request.use((config) => {
      if (tokenRef.current) {
        config.headers['Authorization'] = `Bearer ${tokenRef.current}`;
      }
      return config;
    });
    return () => API.interceptors.request.eject(interceptor);
  }, []);

  const ready = !!token && !loadingToken;

  return (
    <ApiContext.Provider value={{ API, token, loadingToken, ready, error, refetch }}>
      {children}
    </ApiContext.Provider>
  );
}

export function useApi() {
  return useContext(ApiContext);
}
